import { useContext, useEffect, useState } from "react";
import { KanbanBoardContainer, KanbanBoard } from "./KanbanBoardContainer";
import { KanbanCardMemo } from "./KanbanCard";
import KanbanColumn from "./KanbanColumn";
import KanbanItem from "./KanbanItem";
import { KanbanColumnSkeleton, KanbanCardSkeleton } from "./KanbanSkeleton";
import { TaskPopup } from "../Popups/TaskPopup";
import { useUsers } from "../../../hooks/useUsers";
import Delete from "../../Helpers/Delete/DeletePopup";
import {
	useEditTaskMutation,
	useGetAllTasksQuery,
} from "../../../Redux/service/Tasks/Tasks";
import { popupcontext } from "../../../context/Popupscontext";
import { filtrationcontext } from "../../../context/Filtercontext";
import ReschedulePopup from "../Popups/ReschedulePopup";
import { toast } from "react-toastify";
import trans from "../../../utils";
import { useSelector } from "react-redux";
import { useTechnicians } from "../../../hooks/useTechnicians";
import usePermission from "../../../hooks/usePermission";
import {
	KANBAN_COLUMN_DEFINITIONS,
	ALL_KANBAN_COLUMN_IDS,
} from "./kanbanColumns";

const TasksBoard = ({
	fromDate,
	toDate,
	setRefresh,
	refresh,
	setRefreshing,
	viewMode,
	serviceRequestId,
	visibleColumnIds = ALL_KANBAN_COLUMN_IDS,
}) => {
	const columns = KANBAN_COLUMN_DEFINITIONS.filter((col) =>
		visibleColumnIds.includes(col.id)
	);
	const { handleShow, edited, setEdited, setDeleteItem, deleteItem } =
		useContext(popupcontext);
	const { searchQuery, setSearchQuery } = useContext(filtrationcontext);
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [selectedTask, setSelectedTask] = useState("");
	const [deleteTask, setDeleteTask] = useState("");
	const { technicians: users } = useTechnicians();
	const [rescheduleTask, setRescheduleTask] = useState(null);
	const [editTask] = useEditTaskMutation();
	const Livetasks = useSelector((state) => state.webSocket.tasks);
	const {
		data: tasksData,
		isFetching,
		refetch,
	} = useGetAllTasksQuery(
		{
			from: serviceRequestId ? undefined : fromDate,
			to: serviceRequestId ? undefined : toDate,
			search: searchQuery,
			serviceRequestId,
		},
		{ refetchOnMountOrArgChange: true }
	);
	const [tasks, setTasks] = useState([]);

	const { edit, delete: canDelete } = usePermission("FIELD_TASKS");

	useEffect(() => {
		setRefreshing?.(isFetching);
	}, [isFetching, setRefreshing]);

	useEffect(() => {
		if (tasksData?.data) {
			setTasks(tasksData?.data || []);
		}
	}, [tasksData]);

	useEffect(() => {
		async function fetchdata() {
			if (edited) {
				await refetch();
				setEdited(false);
			}
			if (refresh) {
				await refetch();
				setRefresh(false);
			}
			if (Livetasks) {
				await refetch();
			}
		}
		fetchdata();
	}, [edited, refresh, Livetasks]);

	useEffect(() => {
		return () => {
			setSearchQuery("");
		};
	}, [setSearchQuery]);

	useEffect(() => {
		async function fetchDataAfterDelete() {
			if (deleteItem.condition === true && deleteItem.value === "task") {
				await refetch();
				setDeleteItem({ condition: null, value: null });
			}
		}
		fetchDataAfterDelete();
	}, [deleteItem.condition, deleteItem.value, refetch, setDeleteItem]);

	const filterTasksBystatus = (status) => {
		if (!Array.isArray(tasks)) return [];

		return tasks.filter((task) => task.status === status);
	};

	if (isFetching || !Array.isArray(tasks)) return <PageSkeleton />;

	const handleOnDragEnd = async (event) => {
		const stageId = event.over?.id;
		const taskId = event.active?.id;
		const prevStageId = event.active.data.current?.stageId;

		if (!stageId || !taskId || prevStageId === stageId) return;

		const task = tasks.find((t) => t.id === taskId);
		if (!task) return;
		if (stageId === "unAssigned") {
			try {
				await editTask({
					id: String(taskId),
					payload: { status: "unAssigned" },
				}).unwrap();
				setTasks((prev) =>
					prev.map((t) => (t.id === taskId ? { ...t, status: stageId } : t))
				);
				toast.success(trans(`task status is ${stageId}`), {
					autoClose: 800,
					rtl: localStorage.getItem("lang") === "ar" ? true : false,
				});
			} catch (error) {
				toast.error(trans(error?.data?.message), {
					autoClose: 800,
					rtl: localStorage.getItem("lang") === "ar" ? true : false,
				});
			}
			return;
		}
		if (prevStageId === "unAssigned") {
		}
		if (
			(prevStageId === "inProgress" && stageId === "todo") ||
			(prevStageId === "done" && stageId === "todo")
		) {
			setRescheduleTask({
				...task,
				newStatus: stageId,
				startTime: new Date(task.startTime) > new Date() ? task.startTime : "", // blank if past
				duration: task.expectedDuration || "",
			});
			return;
		}
		const payload = { status: stageId };
		const id = String(taskId);
		try {
			await editTask({ id, payload }).unwrap();
			setTasks((prev) =>
				prev.map((t) => (t.id === taskId ? { ...t, status: stageId } : t))
			);
			toast.success(trans(`task status is ${stageId}`), {
				autoClose: 800,
				rtl: localStorage.getItem("lang") === "ar" ? true : false,
			});
		} catch (error) {
			toast.error(trans(error?.data?.message), {
				autoClose: 800,
				rtl: localStorage.getItem("lang") === "ar" ? true : false,
			});
		}
	};
	return (
		<>
			<KanbanBoardContainer>
				<KanbanBoard onDragEnd={handleOnDragEnd} viewMode={viewMode}>
					{columns.map((col) => (
						<KanbanColumn
							id={col.id}
							title={col.title}
							count={filterTasksBystatus(col.id).length}
							key={col.id}
							viewMode={viewMode}
						>
							{filterTasksBystatus(col.id).map((task) => (
								<KanbanItem
									key={task.id}
									id={task.id}
									data={{ ...task, stageId: col.id }}
									disabled={isDropdownOpen || !edit}
									onClick={() => setSelectedTask(task)}
								>
									<KanbanCardMemo
										{...task}
										dueDate={task.dueDate || undefined}
										users={users}
										canEdit={edit}
										canDelete={canDelete}
										onAssign={async (taskId, user) => {
											try {
												const payload = {
													assignedToId: Number(user?.id) || null,
												};

												await editTask({
													id: String(taskId),
													payload,
												}).unwrap();

												setTasks((prev) =>
													prev.map((t) =>
														t.id === taskId
															? {
																	...t,
																	assignedTo: user,
																	assignedToId: Number(user?.id) || null,
																	status:
																		payload.assignedToId === null
																			? "unAssigned"
																			: "todo",
															  }
															: t
													)
												);

												toast.success(
													user
														? `${trans("Task assigned to")}${user.name}`
														: trans("Task unassigned"),
													{
														autoClose: 800,
														rtl:
															localStorage.getItem("lang") === "ar"
																? true
																: false,
													}
												);
											} catch (error) {
												toast.error(trans(error?.data?.message), {
													autoClose: 800,
													rtl:
														localStorage.getItem("lang") === "ar"
															? true
															: false,
												});
											} finally {
												setIsDropdownOpen(false);
											}
										}}
										isDropdownOpen={isDropdownOpen}
										setIsDropdownOpen={setIsDropdownOpen}
										onView={() => setSelectedTask(task)}
										onDelete={() => setDeleteTask(task)}
										onEdit={() => handleShow("editForm", "task", task.id)}
									/>
								</KanbanItem>
							))}
						</KanbanColumn>
					))}
				</KanbanBoard>
			</KanbanBoardContainer>
			<TaskPopup
				taskId={selectedTask.id}
				task={selectedTask}
				onClose={() => setSelectedTask("")}
			/>
			{rescheduleTask && (
				<ReschedulePopup
					task={rescheduleTask}
					onClose={() => setRescheduleTask(null)}
					onSubmit={async (startTime, duration) => {
						try {
							const res = await editTask({
								id: rescheduleTask.id,
								payload: {
									status: rescheduleTask.newStatus,
									startTime,
								},
							}).unwrap();
							toast.success(
								trans(`task status is ${rescheduleTask.newStatus}`, {
									autoClose: 800,
									rtl: localStorage.getItem("lang") === "ar" ? true : false,
								})
							);

							setTasks((prev) =>
								prev.map((t) =>
									t.id === rescheduleTask.id
										? {
												...t,
												status: rescheduleTask.newStatus,
												startTime,
										  }
										: t
								)
							);

							setRescheduleTask(null);
						} catch (error) {
							const message =
								error?.data?.message || trans("Something went wrong");
							toast.error(message, {
								autoClose: 800,
								rtl: localStorage.getItem("lang") === "ar" ? true : false,
							});
						}
					}}
				/>
			)}
			<Delete taskId={deleteTask.id} />
		</>
	);
};

export default TasksBoard;

const PageSkeleton = () => {
	const columnCount = 6;
	const cardCount = 4;
	return (
		<KanbanBoardContainer>
			{Array.from({ length: columnCount }).map((_, index) => (
				<KanbanColumnSkeleton key={index}>
					{Array.from({ length: cardCount }).map((_, index) => (
						<KanbanCardSkeleton key={index} />
					))}
				</KanbanColumnSkeleton>
			))}
		</KanbanBoardContainer>
	);
};
